import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { gsap, revealUp, prefersReducedMotion } from '../lib/motion';
import AutoVideo from '../lib/AutoVideo';

interface Project {
  title: string;
  tag: string;
  copy: string;
  images: string[];
  /** Cinematic clip generated from the project's own photography. */
  video?: string;
}

const PROJECTS: Project[] = [
  {
    title: 'Planet Fitness ATX',
    tag: 'Commercial',
    copy: 'Ground-up interior build-out of a full-scale commercial fitness facility in Austin.',
    images: ['/images/planet-1.jpg', '/images/planet-2.jpg', '/images/planet-3.jpg'],
  },
  {
    title: 'Leasing Office Transformation',
    tag: 'Commercial Interiors',
    copy: 'A dated greeting area reimagined as a modern first impression for future residents.',
    images: ['/images/renata-1.jpg', '/images/renata-2.jpg', '/images/renata-3.jpg'],
    video: '/videos/clubhouse.mp4',
  },
  {
    title: 'Resident Gym Renovation',
    tag: 'Amenities',
    copy: 'Complete amenity overhaul — flooring, lighting, equipment zones, and finishes.',
    images: ['/images/renata-gym-1.jpg', '/images/renata-gym-2.jpg', '/images/renata-gym-3.jpg'],
  },
  {
    title: 'Turnkey Kitchens',
    tag: 'Unit Interiors',
    copy: 'Full kitchen turns — cabinetry, stone, appliances, and lighting — ready to lease.',
    images: ['/images/renata-kitchen-1.jpg', '/images/renata-kitchen-2.jpg', '/images/renata-kitchen-3.jpg'],
    video: '/videos/kitchen.mp4',
  },
  {
    title: 'Bathroom Remodels',
    tag: 'Unit Interiors',
    copy: 'Precision tile and fixture work across hundreds of units a year.',
    images: [
      '/images/renata-br-1.jpg',
      '/images/renata-br-2.jpg',
      '/images/renata-br-3.jpg',
      '/images/renata-br-4.jpg',
      '/images/renata-br-5.jpg',
    ],
  },
  {
    title: 'Exterior & Landscaping',
    tag: 'Site Work',
    copy: 'Hardscape, planting, and outdoor living that lifts an entire property’s curb appeal.',
    video: '/videos/amenity-court.mp4',
    images: [
      '/images/renata-ext-1.jpg',
      '/images/renata-ext-2.jpg',
      '/images/renata-ext-3.jpg',
      '/images/renata-ext-4.jpg',
      '/images/renata-ext-5.jpg',
      '/images/renata-ext-6.jpg',
      '/images/renata-ext-7.jpg',
    ],
  },
  {
    title: 'Roof Replacement Program',
    tag: 'Exteriors',
    copy: 'More than 100 roofs replaced every year — inspected, torn off, and rebuilt to spec.',
    images: ['/images/roof-2.jpg', '/images/roof-3.jpg', '/images/roof-4.jpg'],
  },
];

function Lightbox({
  project,
  index,
  onClose,
  onNav,
}: {
  project: Project;
  index: number;
  onClose: () => void;
  onNav: (dir: 1 | -1) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNav(1);
      if (e.key === 'ArrowLeft') onNav(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onNav]);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-ink-950/95 backdrop-blur-md"
      onClick={onClose}
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute right-5 top-5 z-10 rounded-full glass p-3 text-steel-200 transition-colors hover:text-gold-300"
      >
        <X className="h-5 w-5" />
      </button>
      <button
        aria-label="Previous image"
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        className="absolute left-3 md:left-8 z-10 rounded-full glass p-3 text-steel-200 transition-colors hover:text-gold-300"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        aria-label="Next image"
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        className="absolute right-3 md:right-8 z-10 rounded-full glass p-3 text-steel-200 transition-colors hover:text-gold-300"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <figure className="max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
        <img
          src={project.images[index]}
          alt={`${project.title} — photo ${index + 1}`}
          className="max-h-[78vh] w-auto rounded-lg shadow-2xl"
        />
        <figcaption className="mt-5 text-center">
          <span className="font-display text-lg text-steel-100">{project.title}</span>
          <span className="ml-3 text-sm text-steel-400">
            {index + 1} / {project.images.length}
          </span>
        </figcaption>
      </figure>
    </div>
  );
}

export default function Projects() {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState<{ project: Project; index: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      revealUp('.prj-head', el);
      const rows = gsap.utils.toArray<HTMLElement>('.prj-row');
      rows.forEach((row) => {
        revealUp(row.querySelectorAll('.prj-reveal'), row);
        if (prefersReducedMotion()) return;
        // subtle parallax on each row's hero image
        const heroImg = row.querySelector('.prj-hero-img');
        if (heroImg) {
          gsap.fromTo(
            heroImg,
            { yPercent: -8, scale: 1.15 },
            {
              yPercent: 8,
              scale: 1.15,
              ease: 'none',
              scrollTrigger: { trigger: row, start: 'top bottom', end: 'bottom top', scrub: true },
            }
          );
        }
      });
    }, el);
    return () => ctx.revert();
  }, []);

  const nav = (dir: 1 | -1) =>
    setActive((a) =>
      a ? { ...a, index: (a.index + dir + a.project.images.length) % a.project.images.length } : a
    );

  return (
    <section ref={ref} id="projects" className="relative bg-ink-900 py-28 md:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="prj-head mb-16 md:mb-28">
          <p className="eyebrow mb-6">Featured Projects</p>
          <h2 className="font-display text-4xl font-medium leading-tight text-steel-100 md:text-6xl">
            The work speaks.
            <br />
            <span className="italic text-gold-300">We just build it.</span>
          </h2>
        </div>

        <div className="space-y-24 md:space-y-36">
          {PROJECTS.map((p, i) => (
            <article
              key={p.title}
              className={`prj-row grid grid-cols-1 items-end gap-8 lg:grid-cols-12 ${
                i % 2 ? 'lg:[&>.prj-media]:order-2' : ''
              }`}
            >
              <div className="prj-media lg:col-span-8">
                <button
                  onClick={() => setActive({ project: p, index: 0 })}
                  className="prj-reveal group relative block h-[52vw] max-h-[560px] min-h-[260px] w-full overflow-hidden rounded-2xl"
                  aria-label={`Open gallery: ${p.title}`}
                >
                  {p.video ? (
                    <AutoVideo
                      src={p.video}
                      poster={p.images[0]}
                      className="absolute inset-0 h-full w-full transition-transform duration-[1.4s] ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div
                      className="prj-hero-img absolute inset-0 bg-cover bg-center will-change-transform transition-transform duration-[1.4s] ease-out group-hover:scale-[1.19]"
                      style={{ backgroundImage: `url("${p.images[0]}")` }}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-950/60 via-transparent to-transparent transition-opacity duration-700 group-hover:opacity-40" />
                  <span className="absolute bottom-5 left-6 rounded-full glass px-4 py-1.5 text-[11px] uppercase tracking-widest2 text-steel-200">
                    {p.tag}
                  </span>
                </button>
              </div>

              <div className="lg:col-span-4">
                <span className="prj-reveal font-display text-sm italic text-gold-400/80">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="prj-reveal mt-3 font-display text-2xl font-medium text-steel-100 md:text-3xl">
                  {p.title}
                </h3>
                <p className="prj-reveal mt-4 text-sm font-light leading-relaxed text-steel-300 md:text-base">
                  {p.copy}
                </p>
                <div className="prj-reveal mt-6 flex gap-3">
                  {p.images.slice(1, 4).map((src, j) => (
                    <button
                      key={src}
                      onClick={() => setActive({ project: p, index: j + 1 })}
                      className="h-16 w-20 overflow-hidden rounded-lg border border-steel-100/10 transition-all duration-300 hover:border-gold-500/60 hover:-translate-y-0.5"
                      aria-label={`Open photo ${j + 2} of ${p.title}`}
                    >
                      <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                    </button>
                  ))}
                  {p.images.length > 4 && (
                    <button
                      onClick={() => setActive({ project: p, index: 4 })}
                      className="flex h-16 w-20 items-center justify-center rounded-lg glass text-xs text-steel-300 transition-colors hover:text-gold-300"
                    >
                      +{p.images.length - 4}
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {active && (
        <Lightbox
          project={active.project}
          index={active.index}
          onClose={() => setActive(null)}
          onNav={nav}
        />
      )}
    </section>
  );
}
